// Starting a compliance statement.
//
// ── WHY THIS COMPONENT HAD TO EXIST ──────────────────────────────────────────
// `createComplianceSubmission` shipped in the API client with ZERO callers. The
// endpoint worked, the editor worked, the customer link worked, the PDF worked —
// and there was no way to begin. The compliance page told operators to "start a
// statement from an RFQ line", and no such action existed in the RFQ UI either.
//
// It went unnoticed because every statement made so far was created by a script
// during internal testing, which is precisely the path a real operator does not
// have. A workflow whose first step is missing is not a workflow.
//
// ── THE SUBJECT PICKER IS SEARCH, NOT A DROPDOWN ─────────────────────────────
// Production has 1,272 trading partners and the list endpoint caps `limit` at
// 200. A <select> would therefore show a silently truncated 200 and the partner
// you wanted would often simply not be in it. The server does the matching.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, Search, X } from 'lucide-react';
import {
  createComplianceSubmission, listTradingPartners,
  type TradingPartnerListItem,
} from '@/lib/api';

interface TemplateOption {
  versionId: string;
  code: string;
  versionLabel: string;
  name: string;
  scope: string;
}

interface Props {
  templates: TemplateOption[];
  onClose: () => void;
  /** Called with the new submission's public id so the caller can open it. */
  onCreated: (submissionPublicId: string) => void;
}

export function NewStatementModal({ templates, onClose, onCreated }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const [versionId, setVersionId] = useState(templates[0]?.versionId ?? '');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [partner, setPartner] = useState<TradingPartnerListItem | null>(null);
  const [notes, setNotes] = useState('');

  // Typing a company name should not issue a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useQuery({
    queryKey: ['trading-partners', 'picker', debounced],
    queryFn: () => listTradingPartners(() => getToken(), { q: debounced, limit: 25 }),
  });

  const options = useMemo(() => data?.tradingPartners ?? [], [data]);

  const create = useMutation({
    mutationFn: () => createComplianceSubmission({
      templateVersionPublicId: versionId,
      subjectPartnerPublicId: partner!.id,
      notes: notes.trim() || null,
    }, () => getToken()),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['compliance-submissions'] });
      onCreated(res.publicId);
    },
  });

  const ready = Boolean(versionId) && Boolean(partner) && !create.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Start a compliance statement"
    >
      <div className="card w-full max-w-lg mt-12 p-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold">Start a statement</h2>
          <button onClick={onClose} className="btn-ghost p-1" aria-label="Close">
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* ---- Template ---- */}
          <div>
            <label htmlFor="stmt-template" className="block text-xs font-medium mb-1">
              Statement type
            </label>
            <select
              id="stmt-template"
              className="input text-xs w-full"
              value={versionId}
              onChange={(e) => setVersionId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.versionId} value={t.versionId}>
                  {t.code} {t.versionLabel} — {t.name}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-xs text-destructive mt-1">
                No published template. Publish the standard EUS first.
              </p>
            )}
          </div>

          {/* ---- Subject company ---- */}
          <div>
            <label htmlFor="stmt-partner" className="block text-xs font-medium mb-1">
              Subject company
            </label>
            {partner ? (
              <div className="flex items-center justify-between gap-2 rounded border border-border px-3 py-2">
                <span className="text-xs truncate">
                  {partner.legalName}
                  {partner.countryCode ? (
                    <span className="text-foreground-muted"> · {partner.countryCode}</span>
                  ) : null}
                </span>
                <button
                  onClick={() => { setPartner(null); setSearch(''); }}
                  className="btn-ghost text-xs flex-shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search
                    className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted"
                    aria-hidden
                  />
                  <input
                    id="stmt-partner"
                    className="input text-xs w-full pl-8"
                    placeholder="Search companies by name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoComplete="off"
                  />
                  {isFetching && (
                    <Loader2
                      className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-foreground-muted"
                      aria-hidden
                    />
                  )}
                </div>
                <ul className="mt-1 max-h-52 overflow-y-auto rounded border border-border divide-y divide-border">
                  {options.length === 0 && !isFetching && (
                    <li className="px-3 py-2 text-xs text-foreground-muted">
                      {debounced ? 'No company matches that name.' : 'Type to search.'}
                    </li>
                  )}
                  {options.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => setPartner(p)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-background-subtle"
                      >
                        <span className="truncate block">{p.legalName}</span>
                        <span className="text-foreground-muted text-[11px]">
                          {[
                            p.countryCode,
                            p.roles.customer ? 'customer' : null,
                            p.roles.vendor ? 'vendor' : null,
                            p.status,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-foreground-muted mt-1">
                  The company the statement is ABOUT. Its legal name is suggested as the buyer and
                  the customer can correct it.
                </p>
              </>
            )}
          </div>

          {/* ---- Notes ---- */}
          <div>
            <label htmlFor="stmt-notes" className="block text-xs font-medium mb-1">
              Internal note <span className="text-foreground-muted">(optional)</span>
            </label>
            <input
              id="stmt-notes"
              className="input text-xs w-full"
              placeholder="Why this statement is being collected"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
            />
            <p className="text-[11px] text-foreground-muted mt-1">
              Never shown to the customer.
            </p>
          </div>

          {create.isError && (
            <p className="text-xs text-destructive">
              {(create.error as { message?: string })?.message ?? 'Could not create the statement'}
            </p>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button
            onClick={() => create.mutate()}
            disabled={!ready}
            className="btn-primary text-xs"
          >
            {create.isPending ? 'Creating…' : 'Create statement'}
          </button>
        </div>
      </div>
    </div>
  );
}
